import { isSuccessfulStatusCode } from '../../../helpers/api-ts/request'
import { convertDateToApiParamDateTime } from '../../../helpers/date'
import { authorizedFetch } from '../../helpers/authorizedFetch'
import { formatDate } from '../../helpers/date'
import trans from '../../helpers/trans'
import { generateApiUrl, generateApiV3Url } from '../../helpers/url'
import isResourceCollectionDocument from '../../japi/guards/isResourceCollectionDocument'
import isResourceDocument from '../../japi/guards/isResourceDocument'
import { JapiDocument } from '../../japi/types/Document'
import { DateFormat } from '../../types/dateFormatTypes'
import {
    Pagination,
    PaginationRequestApiParams,
    PaginationV3RequestApiParams,
} from '../../types/paginations'
import { TrackExportType } from '../../types/trackExportType'
import { TrackType } from '../../types/trackTypes'
import { FetchCollectionResult, FetchResult, FetchResultType } from '../FetchResult'
import { NotificationCenterHeaderName, NotificationCenterLog } from '../NotificationCenter/NotificationCenter'
import { isPaginationV3Resource } from '../Pagination/PaginationHelpers'
import { transformToPagination } from '../Pagination/PaginationTransformers'
import { Period } from '../Period/Period'
import {
    ExportTracksFormData,
    FinishedTracksSorting,
    Track,
    TrackExportData,
    TrackFormData,
    TrackResource,
    TracksFilters,
} from './Track'
import { getApprovedFilterValue } from './TrackHelpers'
import {
    getTrackFromApiData,
    transformTrackExportResponseToTrackExportData,
    transformTrackIndexV3Resource,
} from './TrackTransformers'

const trackIncludes = [
    'department',
    'department.group',
    'owner.departments',
    'owner.employmentType',
    'owner.experience',
    'owner.person',
    'owner.roles',
    'shift.department',
    'shift.department.group',
    'shift.shiftPlannings',
    'shift.shiftPlannings.user',
    'shift.shiftPlannings.user.roles',
    'shift.shiftPlannings.user.departments',
    'shift.shiftPlannings.user.employmentType',
    'shift.shiftPlannings.user.experience',
    'feedback',
    'assessor',
    'assessor.roles',
    'comments',
    'comments.owner',
]

/**
 * @deprecated Use getTracksV3ApiCall and getTracksPaginationV3ApiCall instead.
 */
export const getTracksApiCall = async (
    period: Period,
    filters: TracksFilters,
    pagination?: PaginationRequestApiParams,
    signal?: AbortSignal,
): Promise<FetchCollectionResult<Track[], string>> => {
    try {
        const {
            departments,
            userIds,
            trackType,
            employeeSearch,
            employmentTypes,
        } = filters

        const approved = getApprovedFilterValue(trackType)

        const url = generateApiUrl({
            endpoint: '/tracks',
            queryParams: {
                ...((departments && departments.length) && { 'departments[]': departments }),
                ...((userIds && userIds.length) && { userUuids: JSON.stringify(userIds) }),
                ...(employeeSearch && { search: employeeSearch }),
                ...((employmentTypes && employmentTypes.length) && { employmentTypes: JSON.stringify(employmentTypes) }),
                startDate: formatDate(period.start, DateFormat.apiDateTime),
                endDate: formatDate(period.end, DateFormat.apiDateTime),
                include: trackIncludes.join(','),
                ...(approved !== undefined && { approved }),
                // Omit the filter entirely (rather than sending `false`) when no explicit
                // open/closed filter is requested, so both open and closed tracks are returned.
                ...(trackType === TrackType.open && { withOpenCheckOut: true }),
            },
            pageSize: pagination?.size,
            pageNumber: pagination?.number,
        })

        const response = await authorizedFetch(url, { signal })

        const doc: JapiDocument = await response.json()

        if (! isResourceCollectionDocument<TrackResource>(doc) || ! doc.data) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        const tracks = doc.data.map(track => getTrackFromApiData(doc, track))

        return {
            amountOfPages: doc.meta?.pagination.pages || 1,
            status: response.status,
            type: FetchResultType.Success,
            data: tracks,
        }
    } catch (error) {
        if ((error as Error).name === 'AbortError') {
            throw error
        }

        console.error('[getDashboardTracksApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

const getTracksV3QueryParams = (
    period: Period,
    filters: TracksFilters,
    pagination: PaginationV3RequestApiParams,
    sorting?: FinishedTracksSorting,
): Record<string, string | number | boolean | string[]> => {
    const approved = getApprovedFilterValue(filters.trackType)

    return {
        startDate: formatDate(period.start, DateFormat.apiDateTime),
        endDate: formatDate(period.end, DateFormat.apiDateTime),
        start: pagination.start,
        items: pagination.items,
        ...((filters.departments && filters.departments.length) && { 'departments[]': filters.departments }),
        ...((filters.userIds && filters.userIds.length) && { 'userUuids[]': filters.userIds }),
        ...(filters.employeeSearch && { search: filters.employeeSearch }),
        ...((filters.employmentTypes && filters.employmentTypes.length) && { 'employmentTypes[]': filters.employmentTypes }),
        ...(approved !== undefined && { approved }),
        withOpenCheckOut: filters.trackType === TrackType.open,
        ...(sorting && {
            orderBy: sorting.active,
            direction: sorting.direction,
        }),
    }
}

export const getTracksV3ApiCall = async (
    period: Period,
    filters: TracksFilters,
    pagination: PaginationV3RequestApiParams,
    signal?: AbortSignal,
    sorting?: FinishedTracksSorting,
): Promise<FetchCollectionResult<Track[], string>> => {
    try {
        const url = generateApiV3Url({
            endpoint: '/tracks',
            queryParams: getTracksV3QueryParams(period, filters, pagination, sorting),
        })
        const response = await authorizedFetch(url, { method: 'GET', signal })

        if (! isSuccessfulStatusCode(response.status)) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        const data = await response.json()

        if (! Array.isArray(data)) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        const tracks = data.map(transformTrackIndexV3Resource)

        return {
            amountOfPages: 1,
            status: response.status,
            type: FetchResultType.Success,
            data: tracks,
        }
    } catch (error) {
        if ((error as Error).name === 'AbortError') {
            throw error
        }

        console.error('[getTracksV3ApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const getTracksPaginationV3ApiCall = async (
    period: Period,
    filters: TracksFilters,
    pagination: PaginationV3RequestApiParams,
    signal?: AbortSignal,
    sorting?: FinishedTracksSorting,
): Promise<FetchResult<Pagination, string>> => {
    try {
        const url = generateApiV3Url({
            endpoint: '/tracks/meta',
            queryParams: getTracksV3QueryParams(period, filters, pagination, sorting),
        })
        const response = await authorizedFetch(url, { method: 'GET', signal })

        if (! isSuccessfulStatusCode(response.status)) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        const data = await response.json()

        if (! isPaginationV3Resource(data)) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: transformToPagination(data),
        }
    } catch (error) {
        if ((error as Error).name === 'AbortError') {
            throw error
        }

        console.error('[getTracksPaginationV3ApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const getUserExportCsvApiCall = async (formData: ExportTracksFormData): Promise<FetchResult<TrackExportData, string>> => {
    try {
        const {
            payrollPeriodIds,
            departmentIds,
            employmentTypeIds,
            search,
            userIds,
            employeeContractTypes,
        } = formData

        const url = generateApiUrl({
            endpoint: '/users/export/csv',
            queryParams: {
                ...((payrollPeriodIds || []) && { payrollPeriods: JSON.stringify(payrollPeriodIds) }),
                ...((employmentTypeIds || []).length && { employmentTypes: JSON.stringify(employmentTypeIds) }),
                ...((departmentIds || []).length && { departments: JSON.stringify(departmentIds) }),
                ...((userIds || []).length && { users: JSON.stringify(userIds) }),
                ...(employeeContractTypes && employeeContractTypes.length && { employmentTypes: JSON.stringify(employeeContractTypes) }),
                ...(search && { search }),
                'page[number]': 1,
            },
        })

        const response = await authorizedFetch(url)
        const blob = await response.blob()

        if (response.status !== 200 || ! blob) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        const data = transformTrackExportResponseToTrackExportData(
            window.URL.createObjectURL(blob),
            trans('entities.tracks.fileNames.users'),
            'csv',
            formData,
        )

        return {
            status: response.status,
            type: FetchResultType.Success,
            data,
        }
    } catch (error) {
        console.error('[getUserExportCsvApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const getUserExportLoketCsvApiCall = async (formData: ExportTracksFormData): Promise<FetchResult<TrackExportData, string>> => {
    try {
        const {
            payrollPeriodIds,
            departmentIds,
            search,
        } = formData

        const url = generateApiUrl({
            endpoint: '/users/export/loket-csv',
            queryParams: {
                ...(payrollPeriodIds?.length && { payrollPeriod: payrollPeriodIds[0] }),
                ...((departmentIds || []).length && { departments: JSON.stringify(departmentIds) }),
                ...(search && { search }),
                'page[number]': 1,
            },
        })

        const response = await authorizedFetch(url)
        const blob = await response.blob()

        if (response.status !== 200 || ! blob) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        const data = transformTrackExportResponseToTrackExportData(
            window.URL.createObjectURL(blob),
            trans('entities.tracks.fileNames.usersLoket'),
            'csv',
            formData,
        )

        return {
            status: response.status,
            type: FetchResultType.Success,
            data,
        }
    } catch (error) {
        console.error('[getUserExportLoketCsvApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const getTracksCsvApiCall = async (formData: ExportTracksFormData): Promise<FetchResult<TrackExportData, string>> => {
    try {
        const {
            payrollPeriodIds,
            departmentIds,
            employmentTypeIds,
            endDate,
            search,
            startDate,
            employeeContractTypes,
        } = formData

        const queryParams: Record<string, string | number | boolean> = {
            ...(payrollPeriodIds?.length && { payrollPeriod: payrollPeriodIds[0] }),
            ...((employmentTypeIds || []).length && { employmentTypes: JSON.stringify(employmentTypeIds) }),
            ...(employeeContractTypes && employeeContractTypes.length && { employmentTypes: JSON.stringify(employeeContractTypes) }),
            ...(search && { search }),
            approved: 1,
            startDate: formatDate(startDate, DateFormat.apiDateTime),
            endDate: formatDate(endDate, DateFormat.apiDateTime),
            'page[number]': 1,
        }

        if (departmentIds) {
            departmentIds.forEach((departmentId, index) => {
                queryParams[`departments[${index}]`] = departmentId
            })
        }

        const url = generateApiV3Url({
            endpoint: '/tracks/csv',
            queryParams,
        })

        const response = await authorizedFetch(url)
        const blob = await response.blob()

        if (response.status !== 200 || ! blob) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        const data = transformTrackExportResponseToTrackExportData(
            window.URL.createObjectURL(blob),
            trans('entities.tracks.fileNames.tracks'),
            'csv',
            formData,
        )

        return {
            status: response.status,
            type: FetchResultType.Success,
            data,
        }
    } catch (error) {
        console.error('[getTracksCsvApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const getExportWorkCostsApiCall = async (formData: ExportTracksFormData): Promise<FetchResult<TrackExportData, string>> => {
    try {
        const { endDate, startDate } = formData

        const url = generateApiV3Url({
            endpoint: '/export/work-costs',
            queryParams: {
                start: formatDate(startDate, DateFormat.apiDateTime),
                end: formatDate(endDate, DateFormat.apiDateTime),
            },
        })

        const response = await authorizedFetch(url)
        const blob = await response.blob()

        if (response.status !== 200 || ! blob) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        const data = transformTrackExportResponseToTrackExportData(
            window.URL.createObjectURL(blob),
            trans('entities.tracks.fileNames.workRelatedCostsScheme'),
            'xlsx',
            formData,
        )

        return {
            status: response.status,
            type: FetchResultType.Success,
            data,
        }
    } catch (error) {
        console.error('[getExportWorkCostsApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const getExportTracksApiCall = async (formData: ExportTracksFormData): Promise<FetchResult<TrackExportData, string>> => {
    if (formData.exportType === TrackExportType.hourOverview) {
        return getUserExportCsvApiCall(formData)
    }

    if (formData.exportType === TrackExportType.loket) {
        return getUserExportLoketCsvApiCall(formData)
    }

    if (formData.exportType === TrackExportType.workRelatedCostsScheme) {
        return getExportWorkCostsApiCall(formData)
    }

    return getTracksCsvApiCall(formData)
}

/**
 * @deprecated V3 write endpoints are not available yet. Prefer keeping mutations on V2 until a V3 replacement exists.
 */
export const postTrackApiCall = async (
    data: TrackFormData,
    userId: string,
    departmentId: string,
    eventLogTrackerData: NotificationCenterLog,
): Promise<FetchResult<Track, string>> => {
    try {
        const body = {
            data: {
                type: 'tracks',
                attributes: {
                    checkIn: convertDateToApiParamDateTime(data.checkIn),
                    ...(data.checkOut && { checkOut: convertDateToApiParamDateTime(data.checkOut) }),
                    billableStart: convertDateToApiParamDateTime(data.checkIn),
                    ...(data.checkOut && { billableEnd: convertDateToApiParamDateTime(data.checkOut) }),
                    ...(data.meal && { meal: data.meal }),
                    ...(data.pause && { pause: data.pause }),
                    accepted: data.accepted,
                },
                relationships: {
                    owner: {
                        data: {
                            type: 'users',
                            id: userId,
                        },
                    },
                    department: {
                        data: {
                            type: 'departments',
                            id: departmentId,
                        },
                    },
                },
            },
        }

        const url = generateApiUrl({ endpoint: '/tracks', queryParams: { include: trackIncludes.join(',') } })
        const response = await authorizedFetch(url, {
            method: 'POST',
            body: JSON.stringify(body),
            headers: {
                [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
            },
        })

        const doc: JapiDocument = await response.json()

        if (! isResourceDocument<TrackResource>(doc) || ! doc.data) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: doc.errors[0].detail || trans('errors.unknownError'),
            }
        }

        const track = getTrackFromApiData(doc, doc.data)

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: track,
        }
    } catch (error) {
        console.error('[postTrackApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

/**
 * @deprecated V3 write endpoints are not available yet. Prefer keeping mutations on V2 until a V3 replacement exists.
 */
export const patchTrackApiCall = async (
    data: Partial<TrackFormData>,
    trackId: string,
    departmentId: string | undefined,
    eventLogTrackerData: NotificationCenterLog,
): Promise<FetchResult<Track, string>> => {
    try {
        const body = {
            data: {
                type: 'tracks',
                id: trackId,
                attributes: {
                    ...data,
                    checkIn: data.checkIn ? convertDateToApiParamDateTime(data.checkIn) : undefined,
                    checkOut: data.checkOut ? convertDateToApiParamDateTime(data.checkOut) : undefined,
                    billableStart: data.checkIn ? convertDateToApiParamDateTime(data.checkIn) : undefined,
                    billableEnd: data.checkOut ? convertDateToApiParamDateTime(data.checkOut) : undefined,
                },
                ...(departmentId && {
                    relationships: {
                        department: {
                            data: {
                                id: departmentId,
                                type: 'departments',
                            },
                        },
                    },
                }),
            },
        }

        const url = generateApiUrl({ endpoint: `/tracks/${trackId}`, queryParams: { include: trackIncludes.join(',') } })
        const response = await authorizedFetch(url, {
            method: 'PATCH',
            body: JSON.stringify(body),
            headers: {
                [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
            },
        })

        const doc: JapiDocument = await response.json()

        if (! isResourceDocument<TrackResource>(doc) || ! doc.data) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: doc.errors[0].detail || trans('errors.unknownError'),
            }
        }

        const track = getTrackFromApiData(doc, doc.data)

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: track,
        }
    } catch (error) {
        console.error('[patchTrackApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

/**
 * @deprecated V3 write endpoints are not available yet. Prefer keeping mutations on V2 until a V3 replacement exists.
 */
export const deleteTrackApiCall = async (
    trackId: string,
    eventLogTrackerDate: NotificationCenterLog,
): Promise<FetchResult<void, string>> => {
    try {
        const url = generateApiUrl({ endpoint: `/tracks/${trackId}` })

        const response = await authorizedFetch(url, {
            headers: {
                [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerDate),
            },
            method: 'DELETE',
        })

        if (! response.ok) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: undefined,
        }
    } catch (error) {
        console.error('[deleteTrackApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

/**
 * @deprecated Use getTracksV3ApiCall instead when reading track status/date from the index, or keep on V2 until a V3 detail endpoint exists.
 */
export const getTrackStatusAndDateApiCall = async (
    trackId: string,
): Promise<FetchResult<Pick<TrackResource['attributes'], 'accepted' | 'checkIn'>, string>> => {
    try {
        const url = generateApiUrl({ endpoint: `/tracks/${trackId}` })

        const response = await authorizedFetch(url, {
            method: 'GET',
        })

        const doc: JapiDocument = await response.json()

        if (! isResourceDocument<TrackResource>(doc) || ! doc.data) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: doc.errors[0].detail || trans('errors.unknownError'),
            }
        }

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: {
                accepted: doc.data.attributes.accepted,
                checkIn: doc.data.attributes.checkIn,
            },
        }
    } catch (error) {
        console.error('[getTrackStatusApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export interface HasOverlapResponse {
    hasOverlap: boolean;
}

export const checkTrackOverlapApiCall = async (
    userId: string,
    checkIn: Date,
    checkOut: Date,
    excludeTrackId?: string,
): Promise<FetchResult<HasOverlapResponse, string>> => {
    try {
        const url = generateApiUrl({
            endpoint: '/tracks/has-overlap',
            queryParams: {
                userId,
                checkIn: formatDate(checkIn, DateFormat.apiDateTime),
                checkOut: formatDate(checkOut, DateFormat.apiDateTime),
                ...(excludeTrackId && { excludeTrackId }),
            },
        })

        const response = await authorizedFetch(url)
        const data = await response.json()

        if (! response.ok) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: data.error || trans('errors.unknownError'),
            }
        }

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: { hasOverlap: data.hasOverlap },
        }
    } catch (error) {
        console.error('[checkTrackOverlapApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}
